import { useState } from "react";
import { Alert, Button, TextArea } from "./ui.tsx";
import { ApiError, post } from "../lib/api.ts";
import { STATUS_LABEL, type InitiativeStatus } from "../../shared/planning.ts";

export interface Affected { id: string; title: string; status: InitiativeStatus; version: number }

/**
 * Structural impact (docs/02): shows current vs proposed values and the blocked initiatives. Only the instructor
 * can confirm with a reason; the change and every reopening happen atomically, or nothing changes.
 */
export function ContextImpact({ planId, isInstructor, entityType, entityId, expectedVersion, current, changes, affected: initial, onDone, onCancel }: {
  planId: string;
  isInstructor: boolean;
  entityType: "axis" | "objective" | "key_result";
  entityId: string;
  expectedVersion: number;
  current: string;
  changes: Record<string, string | null>;
  affected: Affected[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const [affected, setAffected] = useState(initial);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [busy, setBusy] = useState(false);
  const proposed = changes.name ?? changes.title ?? "";

  async function confirm() {
    if (!reason.trim()) return setError("Informe a justificativa da reabertura.");
    setBusy(true);
    setError(null);
    try {
      await post(`/plans/${planId}/context-change`, {
        entity_type: entityType, entity_id: entityId, expected_version: expectedVersion, changes,
        affected: affected.map((a) => ({ id: a.id, version: a.version })), reason,
      });
      onDone();
    } catch (e) {
      if (e instanceof ApiError && e.code === "CONTEXT_REOPEN_REQUIRED") {
        setAffected((e.details?.affected as Affected[]) ?? []);
        setStale(true);
      } else setError(e instanceof Error ? e.message : "Não foi possível aplicar a alteração.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Alert tone="warn" title="Esta alteração muda o sentido de iniciativas bloqueadas">
      <dl className="mt-1 space-y-0.5">
        <div><dt className="inline font-semibold">Valor atual: </dt><dd className="inline">{current}</dd></div>
        <div><dt className="inline font-semibold">Proposta: </dt><dd className="inline">{proposed || "(descrição alterada)"}</dd></div>
      </dl>
      <p className="mt-2 font-semibold">{affected.length} iniciativa(s) afetada(s):</p>
      <ul className="list-disc pl-5">{affected.map((a) => <li key={a.id}>{a.title} — {STATUS_LABEL[a.status]}</li>)}</ul>
      {stale && <p className="mt-2 font-semibold">As iniciativas afetadas mudaram desde a análise. Confira a lista atualizada antes de confirmar.</p>}
      {isInstructor ? (
        <>
          <p className="mt-2">Ao confirmar, a alteração é aplicada e todas essas iniciativas voltam a rascunho. As versões submetidas e decisões continuam no histórico.</p>
          <div className="mt-2 rounded-ctl bg-white p-3 text-ink">
            <TextArea label="Justificativa" required rows={2} value={reason} error={error ?? undefined} onChange={(e) => { setReason(e.target.value); setError(null); }} />
            <div className="flex flex-wrap gap-2">
              <Button loading={busy} onClick={() => void confirm()}>Confirmar e reabrir {affected.length}</Button>
              <Button variant="secondary" disabled={busy} onClick={onCancel}>Cancelar sem alterar</Button>
            </div>
          </div>
        </>
      ) : (
        <p className="mt-2">Somente o instrutor pode confirmar esta alteração. Nada foi modificado.</p>
      )}
    </Alert>
  );
}
